import React, { useState, useRef } from 'react';
import { FileText, X, Loader2, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, selectClass, labelClass } from './vaultUtils';

const WillsEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    will_type: entry?.data?.will_type || '',
    executor_name: entry?.data?.executor_name || '',
    executor_mobile: entry?.data?.executor_mobile || '',
    executor_email: entry?.data?.executor_email || '',
    send_to_executor: false,
    witness_1: entry?.data?.witness_1 || '',
    witness_2: entry?.data?.witness_2 || '',
    date_executed: entry?.data?.date_executed || '',
    lawyer_name: entry?.data?.lawyer_name || '',
    lawyer_contact: entry?.data?.lawyer_contact || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="wills-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Will Document' : 'Edit Will Document'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., Main Will 2024" className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Type of Will</Label>
              <select name="will_type" value={formData.will_type} onChange={handleChange} className={selectClass}>
                <option value="">Select</option>
                <option value="Simple Will">Simple Will</option>
                <option value="Testamentary Trust Will">Testamentary Trust Will</option>
                <option value="Joint Will">Joint Will</option>
                <option value="Living Will">Living Will</option>
                <option value="Pour-Over Will">Pour-Over Will</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Date Executed</Label>
              <Input type="date" name="date_executed" value={formData.date_executed} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Executor Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Executor Name</Label>
                <Input name="executor_name" value={formData.executor_name} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Executor Mobile</Label>
                <Input name="executor_mobile" value={formData.executor_mobile} onChange={handleChange} placeholder="Mobile number" className={inputClass} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Executor Email</Label>
              <Input 
                type="email" 
                name="executor_email" 
                value={formData.executor_email} 
                onChange={handleChange} 
                placeholder="executor@example.com" 
                className={inputClass} 
              />
            </div>
            
            {/* Send to Executor Toggle */}
            {formData.executor_email && (
              <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <label className="flex items-center gap-3 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, send_to_executor: !prev.send_to_executor }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${formData.send_to_executor ? 'bg-amber-500' : 'bg-slate-600'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.send_to_executor ? 'left-7' : 'left-1'}`}></div>
                  </button>
                  <div className="flex items-center gap-2">
                    <Mail size={18} className={formData.send_to_executor ? 'text-amber-400' : 'text-slate-400'} />
                    <div>
                      <span className="text-white font-medium">Send to Executor</span>
                      <p className="text-slate-400 text-sm">Email the uploaded will document to the executor</p>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Witnesses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Witness 1</Label>
                <Input name="witness_1" value={formData.witness_1} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Witness 2</Label>
                <Input name="witness_2" value={formData.witness_2} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Lawyer Details (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Lawyer Name</Label>
                <Input name="lawyer_name" value={formData.lawyer_name} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Lawyer Contact</Label>
                <Input name="lawyer_contact" value={formData.lawyer_contact} onChange={handleChange} placeholder="Phone/Email" className={inputClass} />
              </div>
            </div>
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WillsEntryModal;
